import { useEffect, useMemo, useState } from 'react';
import { SecurityModel } from 'api';
import { Security } from 'common/types/company';
import { SecurityOption, UseAllSecurityOptionsProps } from './useAllSecurityOptions.props';

export const useAllSecurityOptions = ({
  companyInfo,
  captableInfo,
  capTableVersionsByCompany,
}: UseAllSecurityOptionsProps) => {
  const [securityOptionsByEntity, setSecurityOptionsByEntity] = useState<Record<number, SecurityOption[]>>({});
  const [loading, setLoading] = useState(true);

  const childCompanyIdSecurityMap = useMemo<{ [key: number]: Security[] }>(
    () =>
      Object.values(capTableVersionsByCompany).reduce((acc, holdingCompCaptable) => {
        if (!holdingCompCaptable.length) {
          return acc;
        }

        const primary =
          holdingCompCaptable.find(ct => ct.is_primary)
          ?? holdingCompCaptable.find(ct => (ct.securities?.length ?? 0) > 0)
          ?? holdingCompCaptable[0];

        if (primary && primary.company_id) {
          acc[primary.company_id] = primary.securities || [];
        }
        return acc;
      }, {} as { [key: number]: Security[] }),
    [capTableVersionsByCompany]
  );

  // Securities present in the primary cap table version's fund ownership
  // details. The CFL tab pins captableInfo to the primary version
  // (restrictProformas in useCapTableVersionFilter force-switches proformas),
  // so its fund_ownership IS the primary's. While a non-primary version is
  // transiently loaded its details reference that version's own cloned
  // security ids, so offer nothing rather than a non-primary set. Details
  // with only a custom_security_name carry security=null and are skipped.
  const allowedSecurityIds = useMemo(() => {
    const details = captableInfo?.is_primary === false
      ? []
      : captableInfo?.fund_ownership?.fund_ownership_detail ?? [];
    return new Set(
      details.map(detail => detail.security).filter((id): id is number => id != null)
    );
  }, [captableInfo]);

  // Editor buckets restricted to fund-ownership securities. The unfiltered
  // `securityOptionsByEntity` must stay available so saved rows referencing
  // securities outside fund ownership still render their labels.
  const allowedSecurityOptionsByEntity = useMemo(
    () =>
      Object.entries(securityOptionsByEntity).reduce((acc, [entityId, options]) => {
        acc[Number(entityId)] = options.filter(option => allowedSecurityIds.has(option.id));
        return acc;
      }, {} as Record<number, SecurityOption[]>),
    [securityOptionsByEntity, allowedSecurityIds]
  );

  useEffect(() => {
    setSecurityOptionsByEntity({
      [companyInfo?.id || 0]: (captableInfo?.securities as SecurityModel[] || []).map(
        (security: SecurityModel) => ({
          id: security.id,
          value: security.id,
          label: security.name,
        })
      ),
      ...Object.entries(childCompanyIdSecurityMap).reduce((acc, [childCompanyId, securities]) => {
        acc[Number(childCompanyId)] = securities.map((security: Security) => ({
          id: security.id || 0,
          value: security.id || 0,
          label: security.name || '',
        }));
        return acc;
      }, {} as Record<number, SecurityOption[]>),
    });
    setLoading(false);
  }, [companyInfo, captableInfo, childCompanyIdSecurityMap]);

  return useMemo(
    () => ({
      securityOptionsByEntity,
      allowedSecurityOptionsByEntity,
      loading,
    }),
    [securityOptionsByEntity, allowedSecurityOptionsByEntity, loading]
  );
};
